/**
 * Contractor tax forms.
 *
 * Which form applies follows from the contractor's lane: the Canadian master
 * agreement requires a W-8BEN, the US one a W-9. They behave differently — a
 * W-8BEN expires on the last day of the third succeeding calendar year, a W-9
 * does not expire at all — so the status vocabulary differs between them and
 * the label has to name the right form.
 *
 * This lives in `lib/` rather than inside the component because there is no
 * component-test harness: every web test is a pure-module Bun test, so logic
 * is only testable once it is out of the Lit template.
 */

export const TAX_FORM_W8BEN = 'w-8ben';
export const TAX_FORM_W9 = 'w-9';

export const TAX_FORM_LABELS = {
    [TAX_FORM_W8BEN]: 'W-8BEN',
    [TAX_FORM_W9]: 'W-9',
};

export const TAX_FORM_OPTIONS = [
    { value: TAX_FORM_W8BEN, label: 'W-8BEN (contractor outside the US)' },
    { value: TAX_FORM_W9, label: 'W-9 (US contractor)' },
];

/**
 * The label for a form code, falling back to the raw code so an unrecognised
 * value is visible rather than blank.
 *
 * @param {string} form
 * @returns {string}
 */
export function taxFormLabel(form) {
    return TAX_FORM_LABELS[form] || form || 'Tax form';
}

/**
 * Summarise a contract's tax-form standing for the staff list.
 *
 * Returns `{ text, className }`. Every status the server can send is handled,
 * including a fallback: an unrecognised status previously yielded `undefined`
 * and rendered an empty cell, which reads as "no paperwork" rather than "we
 * don't understand this".
 *
 * @param {{tax_form?: string, tax_form_status?: string, tax_form_valid_through?: string}} contract
 * @returns {{text: string, className: string}}
 */
export function taxFormSummary(contract) {
    const label = taxFormLabel(contract?.tax_form);
    const through = contract?.tax_form_valid_through;

    switch (contract?.tax_form_status) {
        case 'valid':
            return { text: `${label} to ${through}`, className: 'text-success' };
        case 'expiring':
            return { text: `${label} expires ${through}`, className: 'text-warning' };
        case 'expired':
            return { text: `${label} expired ${through}`, className: 'text-danger' };
        case 'on-file':
            // A W-9 — on file, with no expiry to qualify it.
            return { text: `${label} on file`, className: 'text-success' };
        case 'not-on-file':
            return { text: `No ${label}`, className: 'text-muted' };
        default:
            return { text: `${label} status unknown`, className: 'text-muted' };
    }
}

/**
 * Whether a signing date must be recorded for this form.
 *
 * Only a W-8BEN needs one, because its expiry is derived from it. Mirrors the
 * server rule in `handlers/freelancers.go`.
 *
 * @param {string} form
 * @returns {boolean}
 */
export function requiresSigningDate(form) {
    return form === TAX_FORM_W8BEN;
}

/**
 * Until a freelancer can upload their tax form in the app (#249), they
 * download the blank form from the IRS and email the completed one to bizops.
 */
export const BIZOPS_EMAIL = 'bizops@dicedriven.com';

/** The blank IRS form, and the IRS page with its instructions. */
export const TAX_FORM_DOWNLOADS = {
    [TAX_FORM_W9]: {
        url: 'https://www.irs.gov/pub/irs-pdf/fw9.pdf',
        instructionsUrl: 'https://www.irs.gov/forms-pubs/about-form-w-9',
    },
    [TAX_FORM_W8BEN]: {
        url: 'https://www.irs.gov/pub/irs-pdf/fw8ben.pdf',
        instructionsUrl: 'https://www.irs.gov/forms-pubs/about-form-w-8-ben',
    },
};

/**
 * What a freelancer needs to do about their own tax form, or null for
 * nothing: 'send' when none is on file, 'renew' when a W-8BEN has expired or
 * is about to.
 *
 * @param {{ form?: string, status?: string } | null | undefined} taxForm
 *   the `tax_form` from GET /api/msa
 * @returns {'send' | 'renew' | null}
 */
export function taxFormAction(taxForm) {
    if (!taxForm || !TAX_FORM_DOWNLOADS[taxForm.form]) return null;
    if (taxForm.status === 'not-on-file') return 'send';
    if (taxForm.status === 'expired' || taxForm.status === 'expiring') return 'renew';
    return null;
}

/**
 * A mailto link to bizops with the form named in the subject.
 * @param {string} form - a form code
 * @returns {string}
 */
export function bizopsMailto(form) {
    return `mailto:${BIZOPS_EMAIL}?subject=${encodeURIComponent(`My ${taxFormLabel(form)}`)}`;
}
